import { useRef } from "react";
import Me from "../images/kontakt.jpg";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";
import { Helmet } from "react-helmet-async";
import CheckIcon from "@mui/icons-material/Check";

const Slask = () => {
  const imgRef = useRef(null);

  const styles = {
    wrapper: {
      backgroundColor: "#f8fafc",
      minHeight: "100vh",
      padding: "80px 24px 60px 24px",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
    },
    headerZone: {
      textAlign: "center",
      marginBottom: "48px",
    },
    mainTitle: {
      fontSize: "42px",
      fontWeight: "800",
      color: "#0f172a",
      letterSpacing: "-0.03em",
      margin: 0,
    },
    splitLayout: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: "48px",
      alignItems: "start",
    },
    imageColumn: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "sticky",
      top: "40px",
    },
    avatarWrapper: {
      width: "380px",
      height: "280px",
      borderRadius: "10%",
      overflow: "hidden",
      boxShadow: "0 20px 40px -15px rgba(15, 23, 42, 0.15)",
      border: "4px solid #ffffff",
    },
    avatarImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    contentColumn: {
      display: "flex",
      flexDirection: "column",
      gap: "32px",
    },
    introText: {
      fontSize: "16px",
      color: "#334155",
      lineHeight: "1.7",
      margin: 0,
    },
    sectionTitle: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#0f172a",
      margin: "0 0 16px 0",
    },
    listCard: {
      background: "#ffffff",
      borderRadius: "20px",
      padding: "32px",
      boxShadow: "0 4px 30px -10px rgba(0,0,0,0.02)",
      border: "1px solid #e2e8f0",
    },
    list: {
      listStyleType: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: "14px",
    },
    listItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: "15px",
      color: "#475569",
      fontWeight: "500",
    },
    checkIcon: {
      color: "#10b981",
      fontSize: "20px",
    },
    faqContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    faqCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 2px 12px -5px rgba(0,0,0,0.02)",
    },
    faqQuestion: {
      fontSize: "16px",
      fontWeight: "700",
      color: "#0f172a",
      margin: "0 0 8px 0",
    },
    faqAnswer: {
      fontSize: "14px",
      color: "#475569",
      lineHeight: "1.6",
      margin: 0,
    },
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Fullstack Developer Śląsk",
            url: "https://michaljaskulski.pl/fullstack-developer-slask",
            telephone: "+48602209858",
            priceRange: "$$",
            image:
              "https://michaljaskulski.pl/static/media/MJ1.5d56ca7382670a51ab2b.JPG",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Mikołów",
              addressCountry: "PL",
            },
            areaServed: [
              {
                "@type": "AdministrativeArea",
                name: "Województwo śląskie",
              },
              {
                "@type": "Country",
                name: "Polska",
              },
            ],
            serviceType: "Fullstack Development & Consulting",
            provider: {
              "@type": "Person",
              name: "Michał Jaskulski",
            },
            offers: {
              "@type": "OfferCatalog",
              name: "Oferta programistyczna",
              itemListElement: [
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Aplikacje webowe w React",
                    description:
                      "Budowa dynamicznych aplikacji SPA w React, Next.js, Vite.",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Backend API (Node.js / PHP Slim)",
                    description:
                      "Tworzenie skalowalnego backendu - REST API i integracje z bazami danych.",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Bazy danych MySQL, MSSQL",
                    description:
                      "Projektowanie struktur, optymalizacja zapytań i migracje danych.",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Responsywne strony www",
                    description:
                      "Nowoczesne strony internetowe dopasowane do urządzeń mobilnych.",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Konfiguracja serwerów VPS",
                    description:
                      "Linux, Docker, SSL, backupy i utrzymanie produkcyjne.",
                  },
                },
              ],
            },
          })}
        </script>
        <title>
          Fullstack Developer Śląsk - React, Node.js, Aplikacje Webowe
        </title>
        <meta
          name="description"
          content="Projektowanie i wdrażanie aplikacji webowych dostosowanych do potrzeb biznesowych dla firm z województwa śląskiego"
        />
      </Helmet>

      <div className="bodyBackground" style={styles.wrapper}>
        <AnimatePresence>
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={textVariants}
            style={styles.container}
          >
            <div style={styles.headerZone}>
              <h1 style={styles.mainTitle}>
                Fullstack Developer - województwo śląskie
              </h1>
            </div>
            <div style={styles.splitLayout}>
              <div style={styles.imageColumn}>
                <div style={styles.avatarWrapper}>
                  <img
                    ref={imgRef}
                    src={Me}
                    style={styles.avatarImg}
                    alt="Michał Jaskulski"
                  />
                </div>
              </div>
              <div style={styles.contentColumn}>
                <p style={styles.introText}>
                  Jako fullstack developer realizuję projekty aplikacji webowych
                  oraz systemów biznesowych dla firm z województwa śląskiego.
                  Tworzę nowoczesne rozwiązania oparte o React, Node.js, PHP
                  oraz relacyjne bazy danych.
                </p>

                {/* Sekcja Usług */}
                <div style={styles.listCard}>
                  <h2 style={styles.sectionTitle}>
                    Usługi fullstack developmentu
                  </h2>
                  <ul style={styles.list}>
                    <li style={styles.listItem}>
                      <CheckIcon style={styles.checkIcon} /> Aplikacje webowe
                      SPA (React, Vite)
                    </li>
                    <li style={styles.listItem}>
                      <CheckIcon style={styles.checkIcon} /> Systemy wewnętrzne
                      dla firm
                    </li>
                    <li style={styles.listItem}>
                      <CheckIcon style={styles.checkIcon} /> Backend API
                      (Node.js, PHP Slim)
                    </li>
                    <li style={styles.listItem}>
                      <CheckIcon style={styles.checkIcon} /> Bazy danych MySQL i
                      MSSQL
                    </li>
                    <li style={styles.listItem}>
                      <CheckIcon style={styles.checkIcon} /> Automatyzacja
                      procesów biznesowych
                    </li>
                    <li style={styles.listItem}>
                      <CheckIcon style={styles.checkIcon} /> Wdrożenia na
                      serwerach VPS i Docker
                    </li>
                  </ul>
                </div>

                {/* Sekcja FAQ */}
                <div>
                  <h2 style={styles.sectionTitle}>
                    Fullstack Developer Śląsk – FAQ
                  </h2>
                  <div style={styles.faqContainer}>
                    <div style={styles.faqCard}>
                      <h3 style={styles.faqQuestion}>
                        Czy realizujesz projekty dla firm ze Śląska?
                      </h3>
                      <p style={styles.faqAnswer}>
                        Tak. Współpracuję z firmami z województwa śląskiego oraz
                        całej Polski.
                      </p>
                    </div>

                    <div style={styles.faqCard}>
                      <h3 style={styles.faqQuestion}>
                        Czy możliwa jest współpraca zdalna?
                      </h3>
                      <p style={styles.faqAnswer}>
                        Tak. Projekty realizuję zarówno lokalnie, jak i w pełni
                        zdalnie.
                      </p>
                    </div>

                    <div style={styles.faqCard}>
                      <h3 style={styles.faqQuestion}>
                        Jakie technologie stosujesz?
                      </h3>
                      <p style={styles.faqAnswer}>
                        React, Node.js, PHP, MySQL, MSSQL, Docker, VPS.
                      </p>
                    </div>

                    <div style={styles.faqCard}>
                      <h3 style={styles.faqQuestion}>
                        Czy Twoje aplikacje są bezpieczne?
                      </h3>
                      <p style={styles.faqAnswer}>
                        Tak. Bezpieczeństwo traktuję jako standard, a nie
                        dodatek. Stosuję sprawdzone mechanizmy ochrony danych i
                        dostępu oraz dbam o aktualność i jakość kodu.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  );
};

export default Slask;
